'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PLANS, WHITELISTED_EMAILS, type PlanId, type PlanLimits, getPlanLimits, canAddMore } from '@/lib/plans';

export interface SubscriptionInfo {
  plan: PlanId;
  status: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  paystackCustomerCode: string | null;
  paystackSubscriptionCode: string | null;
  isWhitelisted: boolean;
  isTrialing: boolean;
  isActive: boolean;
  trialDaysLeft: number;
}

export interface SubscriptionContextValue {
  subscription: SubscriptionInfo | null;
  limits: PlanLimits;
  loading: boolean;
  canAddProduct: (currentCount: number) => boolean;
  canAddRawMaterial: (currentCount: number) => boolean;
  canAddUser: (currentCount: number) => boolean;
  canAccessReporting: () => boolean;
  canAccessAdvancedAnalytics: () => boolean;
  canAccessPurchaseHistory: () => boolean;
  canExportXlsx: () => boolean;
  canImportXlsx: () => boolean;
  refreshSubscription: () => Promise<void>;
  planName: string;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({} as SubscriptionContextValue);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { profile, company, user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const loadSubscription = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    try {
      // Check if user email is whitelisted — use profile email OR auth user email as fallback
      const userEmail = (profile?.email ?? user?.email ?? '').toLowerCase();
      const isWhitelisted = WHITELISTED_EMAILS.includes(userEmail);

      const { data } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', company.id)
        .single();

      if (data) {
        const planId = (isWhitelisted || data.plan === 'whitelisted') ? 'whitelisted' : data.plan as PlanId;
        const trialEndsAt = data.trial_ends_at;
        const now = new Date();
        const trialEnd = trialEndsAt ? new Date(trialEndsAt) : null;
        const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;
        const isTrialing = !isWhitelisted && data.status === 'trialing' && trialEnd !== null && trialEnd > now;
        const isActive = data.status === 'active' || isTrialing || planId === 'whitelisted';

        setSubscription({
          plan: planId,
          status: isWhitelisted ? 'active' : data.status,
          trialEndsAt: isWhitelisted ? null : data.trial_ends_at,
          currentPeriodEnd: data.current_period_end,
          paystackCustomerCode: data.paystack_customer_code,
          paystackSubscriptionCode: data.paystack_subscription_code,
          isWhitelisted,
          isTrialing,
          isActive,
          trialDaysLeft: isWhitelisted ? 0 : trialDaysLeft,
        });
      } else {
        // No subscription record — treat as launch_pass trial
        const planId: PlanId = isWhitelisted ? 'whitelisted' : 'launch_pass';
        setSubscription({
          plan: planId,
          status: isWhitelisted ? 'active' : 'trialing',
          trialEndsAt: null,
          currentPeriodEnd: null,
          paystackCustomerCode: null,
          paystackSubscriptionCode: null,
          isWhitelisted,
          isTrialing: !isWhitelisted,
          isActive: true,
          trialDaysLeft: isWhitelisted ? 0 : 7,
        });
      }
    } catch {
      // Non-fatal
    } finally {
      setLoading(false);
    }
  }, [company?.id, profile?.email, user?.email, supabase]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    await loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const effectivePlan: PlanId = subscription?.plan ?? 'launch_pass';
  const limits = getPlanLimits(effectivePlan);

  const canAddProduct = (currentCount: number) => canAddMore(currentCount, limits.products);
  const canAddRawMaterial = (currentCount: number) => canAddMore(currentCount, limits.rawMaterials);
  const canAddUser = (currentCount: number) => canAddMore(currentCount, limits.users);
  const canAccessReporting = () => limits.hasReporting;
  const canAccessAdvancedAnalytics = () => limits.hasAdvancedAnalytics;
  const canAccessPurchaseHistory = () => limits.hasPurchaseHistory;
  const canExportXlsx = () => limits.hasXlsxExport;
  const canImportXlsx = () => limits.hasXlsxImport;

  const planName = PLANS[effectivePlan]?.name ?? 'InventIQ Launch Pass';

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        limits,
        loading,
        canAddProduct,
        canAddRawMaterial,
        canAddUser,
        canAccessReporting,
        canAccessAdvancedAnalytics,
        canAccessPurchaseHistory,
        canExportXlsx,
        canImportXlsx,
        refreshSubscription,
        planName,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
